import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { clsx } from 'clsx';

interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

const DANGER_RE = /eliminar|quitar|revocar|desconectar|desactivar|despublicar|cancelar|suspender|archivar|rechazar|retirar|dejar de|reembols|descartar|reemplazar|deshacer/i;

/**
 * Styled, promise-based replacement for window.confirm():
 *   if (!await confirmDialog('¿Eliminar esta nota? No se puede deshacer.')) return;
 * Defaults are derived from the message so call sites stay one-liners: the first
 * "¿…?" becomes the title and the rest the body, the question's leading verb
 * labels the button ("Eliminar", "Restaurar"…), and destructive wording turns it red.
 */
export function confirmDialog(message: string, opts: ConfirmOptions = {}): Promise<boolean> {
  const props = parseConfirm(message, opts);
  // ponytail: own React root instead of a provider, so non-component code (CRMContext) can call it too.
  const host = document.createElement('div');
  document.body.appendChild(host);
  const root = createRoot(host);
  return new Promise(resolve => {
    let settled = false;
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      resolve(ok);
      setTimeout(() => { root.unmount(); host.remove(); }, 150);
    };
    root.render(<Dialog {...props} done={done} />);
  });
}

export function parseConfirm(message: string, opts: ConfirmOptions = {}) {
  const question = opts.title ? null : message.match(/¿[^?]*\?/)?.[0];
  const title = opts.title ?? question ?? 'Confirmar acción';
  const body = (question ? message.replace(question, '') : message)
    .split('\n').map(l => l.trim()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  const verb = title.match(/¿\s*([A-ZÁÉÍÓÚ][a-záéíóúñ]+(?:ar|er|ir))\b/)?.[1];
  const confirmLabel = opts.confirmLabel ?? verb ?? 'Confirmar';
  const cancelLabel = opts.cancelLabel ?? (confirmLabel === 'Cancelar' ? 'Volver' : 'Cancelar');
  const danger = opts.danger ?? DANGER_RE.test(message);
  return { title, body, confirmLabel, cancelLabel, danger };
}

function Dialog({ title, body, confirmLabel, cancelLabel, danger, done }:
  ReturnType<typeof parseConfirm> & { done: (ok: boolean) => void }) {
  const [closing, setClosing] = useState(false);
  const okRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  // The ThemeContext provider isn't above this root; read the class it puts on <html>.
  const isLight = !document.documentElement.classList.contains('dark-theme');

  const close = (ok: boolean) => {
    setClosing(true);
    done(ok);
  };

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    // Destructive: start on Cancelar so a reflexive Enter doesn't destroy anything.
    (danger ? cancelRef : okRef).current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Capture phase + stop, so the modal underneath doesn't also close.
        e.preventDefault();
        e.stopImmediatePropagation();
        close(false);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        (document.activeElement === okRef.current ? cancelRef : okRef).current?.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, []);

  const Icon = danger ? AlertTriangle : HelpCircle;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-4"
      style={{ animation: closing ? 'fadeOut .15s ease-in forwards' : 'fadeIn .15s ease-out' }}
    >
      <div
        className={clsx('absolute inset-0 backdrop-blur-sm', isLight ? 'bg-slate-900/40' : 'bg-black/60')}
        onClick={() => close(false)}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={body ? 'confirm-dialog-body' : undefined}
        className={clsx(
          'relative w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl',
          isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-700/60'
        )}
        style={{ animation: 'zoomIn .2s cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        <div className="flex gap-4 p-6">
          <div
            className={clsx(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
              danger
                ? (isLight ? 'bg-rose-100 text-rose-600' : 'bg-rose-500/15 text-rose-400')
                : (isLight ? 'bg-indigo-100 text-indigo-600' : 'bg-indigo-500/15 text-indigo-400')
            )}
          >
            <Icon size={22} />
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <h2
              id="confirm-dialog-title"
              className={clsx('text-base font-semibold leading-snug', isLight ? 'text-slate-900' : 'text-white')}
            >
              {title}
            </h2>
            {body && (
              <p
                id="confirm-dialog-body"
                className={clsx(
                  'mt-2 max-h-[50vh] overflow-y-auto overflow-x-hidden break-words whitespace-pre-line text-sm leading-relaxed',
                  isLight ? 'text-slate-600' : 'text-slate-400'
                )}
              >
                {body}
              </p>
            )}
          </div>
        </div>
        <div
          className={clsx(
            'flex flex-col-reverse gap-2 px-6 py-4 sm:flex-row sm:justify-end',
            isLight ? 'bg-slate-50 border-t border-slate-100' : 'bg-white/[0.03] border-t border-white/5'
          )}
        >
          <button
            ref={cancelRef}
            type="button"
            onClick={() => close(false)}
            className={clsx(
              'rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              isLight
                ? 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100 focus-visible:ring-slate-400 focus-visible:ring-offset-white'
                : 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 focus-visible:ring-slate-500 focus-visible:ring-offset-slate-900'
            )}
          >
            {cancelLabel}
          </button>
          <button
            ref={okRef}
            type="button"
            onClick={() => close(true)}
            className={clsx(
              'rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              isLight ? 'focus-visible:ring-offset-white' : 'focus-visible:ring-offset-slate-900',
              danger
                ? 'bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-500'
                : 'bg-indigo-600 hover:bg-indigo-500 focus-visible:ring-indigo-500'
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
